/* eslint-disable max-len */
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { Boundary } from 'components/common';
import { useDocumentTitle, useScrollTop } from 'hooks';
import React, {
  useCallback, useEffect, useMemo, useState
} from 'react';
import PropType from 'prop-types';
import firebase from 'services/firebase';
import {
  formatAccountingMoney,
  getMonthlyReport,
  getOrderCollected,
  getOrderOutstanding,
  getOrderTotals,
  toAmount
} from 'utils/accounting';

const today = () => new Date().toISOString().slice(0, 10);
const currentMonth = () => new Date().toISOString().slice(0, 7);

const COLOR_LABELS = { WHITE: 'أبيض', BLACK: 'أسود' };
const LOSS_TYPE_LABELS = {
  RAW_MATERIAL: 'خامات',
  COMPLETED_STAND: 'منتج مكتمل',
  MONEY: 'أموال'
};
const EXPENSE_CATEGORY_LABELS = {
  ADVERTISING: 'إعلانات ممولة',
  SALARY: 'راتب أو عمولة',
  OTHER: 'مصروف آخر'
};

const getArabicMaterialName = (name = '') => name
  .replace(/WHITE/g, 'أبيض')
  .replace(/BLACK/g, 'أسود')
  .replace(/Raw Acrylic/gi, 'أكريليك خام')
  .replace(/Custom material/gi, 'خامة مخصصة');

const getArabicErrorMessage = (error, fallback) => {
  const message = error && error.message ? error.message : '';
  if (message.includes('Not enough stock')) return 'الكمية الموجودة في المخزون لا تكفي لإتمام هذا الطلب.';
  if (message.includes('raw material no longer exists')) return 'الخامة المحددة لم تعد موجودة.';
  if (message.includes('Loss quantity exceeds')) return 'كمية الهالك أكبر من الكمية المتاحة في المخزون.';
  if (message.includes('courier collection')) return 'لا يمكن حذف طلب مرتبط بتحصيل من شركة الشحن.';
  if (message.includes('collected amount')) return 'لا يمكن جعل قيمة الطلب أقل من المبلغ الذي تم تحصيله بالفعل.';
  if (message.includes('selected order no longer exists')) return 'الطلب المحدد لم يعد موجوداً.';
  if (message.includes('permission') || message.includes('Permission')) return 'ليس لديك صلاحية لتنفيذ هذه العملية.';
  return fallback;
};

const EMPTY_DATA = {
  accountingInventory: [],
  accountingPurchases: [],
  accountingOrders: [],
  accountingLosses: [],
  accountingExpenses: [],
  accountingCollections: [],
  accountingOffers: [],
  accountingSettings: []
};

const DEFAULT_STAND_PRICES = {
  smallWithoutNfc: 450,
  smallWithNfc: 540,
  largeWithoutNfc: 450,
  largeWithNfc: 540,
  circleWithoutNfc: 450,
  circleWithNfc: 540
};

const getStandType = (size = '') => {
  if (size === '15×25') return 'large';
  if (size === '10×10 Circle') return 'circle';
  return 'small';
};

const getStandPrice = (prices, size, nfc) => {
  const type = getStandType(size);
  const key = `${type}${nfc ? 'WithNfc' : 'WithoutNfc'}`;
  return toAmount(prices[key]);
};

const createOrderItem = (unitPrice = DEFAULT_STAND_PRICES.smallWithoutNfc) => ({
  productName: 'ستاند أكريليك',
  productCatalogId: '',
  materialId: '',
  materialQuantityPerUnit: 1,
  nfc: false,
  quantity: 1,
  unitPrice,
  discount: 0,
  customUnitCost: 0
});

const createProductType = () => ({
  id: '',
  name: '',
  hasNfcOptions: false,
  withoutNfcPrice: 0,
  withNfcPrice: 0
});

const createOrder = (unitPrice = DEFAULT_STAND_PRICES.smallWithoutNfc) => ({
  customerName: '',
  date: today(),
  deposit: 0,
  discountPercentage: 0,
  shippingCost: 140,
  shippingCharged: 90,
  status: 'PENDING',
  notes: '',
  items: [createOrderItem(unitPrice)]
});

const sortByDate = (items) => [...items].sort(
  (first, second) => (second.date || '').localeCompare(first.date || '')
);

const groupCollectionRecords = (items) => Object.values(items.reduce((groups, item) => {
  const key = item.batchId || item.id;
  const current = groups[key] || {
    id: key,
    date: item.date,
    note: item.note,
    amount: 0,
    customerNames: [],
    isBatch: Boolean(item.batchId)
  };
  return {
    ...groups,
    [key]: {
      ...current,
      amount: current.amount + toAmount(item.amount),
      customerNames: [...current.customerNames, item.customerName].filter(Boolean)
    }
  };
}, {}));

const allocateCollectionAmount = (amount, selectedOrders, collections) => {
  const totalCents = Math.round(toAmount(amount) * 100);
  const ordersWithBalance = selectedOrders.map((order) => ({
    ...order,
    outstandingCents: Math.round(getOrderOutstanding(order, collections) * 100)
  }));
  const outstandingCents = ordersWithBalance.reduce(
    (total, order) => total + order.outstandingCents,
    0
  );
  const allocations = ordersWithBalance.map((order) => Math.floor(
    (totalCents * order.outstandingCents) / outstandingCents
  ));
  let remainingCents = totalCents - allocations.reduce((total, value) => total + value, 0);

  while (remainingCents > 0) {
    const orderIndex = allocations.findIndex(
      (allocated, index) => allocated < ordersWithBalance[index].outstandingCents
    );
    if (orderIndex < 0) break;
    allocations[orderIndex] += 1;
    remainingCents -= 1;
  }

  return ordersWithBalance.map((order, index) => ({
    id: order.id,
    customerName: order.customerName,
    amount: allocations[index] / 100
  }));
};

const Accounting = () => {
  useDocumentTitle('الحسابات والمخزون | TaKKa');
  useScrollTop();

  const [activeTab, setActiveTab] = useState('overview');
  const [data, setData] = useState(EMPTY_DATA);
  const [isLoading, setLoading] = useState(true);
  const [isSaving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [reportMonth, setReportMonth] = useState(currentMonth());
  const [purchase, setPurchase] = useState({
    name: 'أكريليك خام',
    size: '11×18',
    customSize: '',
    color: 'WHITE',
    quantity: 1,
    unitPrice: 0,
    date: today(),
    note: ''
  });
  const [order, setOrder] = useState(createOrder());
  const [editingOrderId, setEditingOrderId] = useState('');
  const [updateMaterialPrice, setUpdateMaterialPrice] = useState(false);
  const [loss, setLoss] = useState({
    type: 'RAW_MATERIAL', date: today(), materialId: '', quantity: 1, amount: 0, note: ''
  });
  const [expense, setExpense] = useState({
    category: 'ADVERTISING',
    name: 'إعلانات ممولة',
    amount: 0,
    date: today(),
    note: ''
  });
  const [collection, setCollection] = useState({
    orderIds: [], amount: 0, date: today(), note: ''
  });
  const [offer, setOffer] = useState({
    name: 'عرض 3 ستاند NFC صغير',
    quantity: 3,
    regularUnitPrice: 540,
    offerPrice: 1500,
    nfc: true,
    freeShipping: true,
    note: ''
  });
  const [selectedOfferId, setSelectedOfferId] = useState('');
  const [standPrices, setStandPrices] = useState(DEFAULT_STAND_PRICES);
  const [productCatalog, setProductCatalog] = useState([]);
  const [newProductType, setNewProductType] = useState(createProductType());

  const loadAccounting = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const accountingData = await firebase.getAccountingData();
      const savedPrices = (accountingData.accountingSettings || [])
        .find((item) => item.id === 'standPrices');
      const savedProductCatalog = (accountingData.accountingSettings || [])
        .find((item) => item.id === 'productCatalog');
      setData(accountingData);
      setStandPrices({ ...DEFAULT_STAND_PRICES, ...(savedPrices || {}) });
      setProductCatalog(Array.isArray(savedProductCatalog && savedProductCatalog.items)
        ? savedProductCatalog.items
        : []);
    } catch (error) {
      setMessage({
        type: 'error',
        text: getArabicErrorMessage(error, 'تعذر تحميل بيانات الحسابات.')
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccounting();
  }, [loadAccounting]);

  const inventory = data.accountingInventory || [];
  const orders = sortByDate(data.accountingOrders || []);
  const purchases = sortByDate(data.accountingPurchases || []);
  const losses = sortByDate(data.accountingLosses || []);
  const expenses = sortByDate(data.accountingExpenses || []);
  const collections = sortByDate(data.accountingCollections || []);
  const collectionRecords = groupCollectionRecords(collections);
  const offers = data.accountingOffers || [];
  const activeOffers = offers.filter((item) => item.active !== false);
  const shippedOrders = orders.filter((item) => item.status === 'SHIPPED');
  const collectibleOrders = shippedOrders.filter(
    (item) => getOrderOutstanding(item, collections) > 0
  );
  const selectedCollectionOrders = collectibleOrders.filter(
    (item) => collection.orderIds.includes(item.id)
  );
  const selectedCollectionTotal = selectedCollectionOrders.reduce(
    (total, item) => total + getOrderOutstanding(item, collections),
    0
  );
  const report = useMemo(() => getMonthlyReport({
    month: reportMonth,
    orders,
    losses,
    expenses,
    collections
  }), [collections, expenses, losses, orders, reportMonth]);
  const monthlyShippedOrderCount = orders.filter((item) => (
    (item.date || '').startsWith(reportMonth) && item.status === 'SHIPPED'
  )).length;
  const inventoryValue = inventory.reduce(
    (total, item) => total + toAmount(item.inventoryValue),
    0
  );
  const totalOutstanding = orders.reduce(
    (total, item) => total + getOrderOutstanding(item, collections),
    0
  );
  const effectivePurchaseSize = purchase.size === 'Custom'
    ? purchase.customSize.trim()
    : purchase.size;
  const matchingPurchaseItem = inventory.find((item) => (
    item.name.trim().toLowerCase() === purchase.name.trim().toLowerCase()
    && item.size === effectivePurchaseSize
    && item.color === purchase.color
  ));

  const runSave = async (action, successText) => {
    setSaving(true);
    setMessage(null);
    try {
      await action();
      await loadAccounting();
      setMessage({ type: 'success', text: successText });
      return true;
    } catch (error) {
      setMessage({
        type: 'error',
        text: getArabicErrorMessage(error, 'تعذر حفظ العملية المحاسبية.')
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const submitPurchase = async (event) => {
    event.preventDefault();
    if (
      toAmount(purchase.quantity) <= 0
      || (!matchingPurchaseItem && toAmount(purchase.unitPrice) < 0)
      || (updateMaterialPrice && toAmount(purchase.unitPrice) < 0)
    ) {
      setMessage({ type: 'error', text: 'أدخل كمية وسعر شراء صحيحين.' });
      return;
    }
    const saved = await runSave(
      () => firebase.addAccountingStock({
        ...purchase,
        size: effectivePurchaseSize,
        inventoryId: matchingPurchaseItem ? matchingPurchaseItem.id : '',
        quantity: toAmount(purchase.quantity),
        unitPrice: matchingPurchaseItem && !updateMaterialPrice
          ? toAmount(matchingPurchaseItem.averageUnitCost)
          : toAmount(purchase.unitPrice),
        updateUnitPrice: Boolean(updateMaterialPrice)
      }),
      matchingPurchaseItem ? 'تمت إضافة الكمية إلى المخزون بنجاح.' : 'تمت إضافة الخامة بنجاح.'
    );
    if (saved) {
      setPurchase((current) => ({
        ...current, quantity: 1, unitPrice: 0, note: ''
      }));
      setUpdateMaterialPrice(false);
    }
  };

  const updateOrderItem = (index, updates) => setOrder((current) => ({
    ...current,
    items: current.items.map((item, itemIndex) => (
      itemIndex === index ? { ...item, ...updates } : item
    ))
  }));

  const getProductTypePrice = (productType, nfc) => (
    nfc && productType.hasNfcOptions
      ? toAmount(productType.withNfcPrice)
      : toAmount(productType.withoutNfcPrice)
  );

  const getOrderItemPrice = (item, nfc = item.nfc, materialId = item.materialId) => {
    const productType = productCatalog.find((entry) => entry.id === item.productCatalogId);
    if (productType) return getProductTypePrice(productType, nfc);
    const material = inventory.find((entry) => entry.id === materialId);
    return getStandPrice(standPrices, material ? material.size : '', nfc);
  };

  const submitOrder = async (event) => {
    event.preventDefault();
    if (!order.customerName.trim() || !order.items.length) {
      setMessage({ type: 'error', text: 'اسم العميل ومنتج واحد على الأقل مطلوبان.' });
      return;
    }
    if (order.items.some((item) => toAmount(item.quantity) <= 0 || toAmount(item.unitPrice) < 0)) {
      setMessage({ type: 'error', text: 'يجب إدخال كمية وسعر صحيحين لكل منتج.' });
      return;
    }
    if (toAmount(order.discountPercentage) < 0 || toAmount(order.discountPercentage) > 100) {
      setMessage({ type: 'error', text: 'نسبة خصم الطلب يجب أن تكون بين 0 و100%.' });
      return;
    }
    const normalizedOrder = {
      ...order,
      customerName: order.customerName.trim(),
      deposit: toAmount(order.deposit),
      discountPercentage: toAmount(order.discountPercentage),
      shippingCost: toAmount(order.shippingCost),
      shippingCharged: toAmount(order.shippingCharged),
      items: order.items.map((item) => ({
        ...item,
        quantity: toAmount(item.quantity),
        unitPrice: toAmount(item.unitPrice),
        discount: toAmount(item.discount),
        materialQuantityPerUnit: toAmount(item.materialQuantityPerUnit) || 1,
        customUnitCost: toAmount(item.customUnitCost)
      }))
    };
    const saved = await runSave(
      () => (editingOrderId
        ? firebase.updateAccountingOrder(editingOrderId, normalizedOrder)
        : firebase.createAccountingOrder(normalizedOrder)),
      editingOrderId
        ? 'تم تحديث الطلب وتسوية المخزون بنجاح.'
        : 'تم تسجيل الطلب وخصم الخامات من المخزون بنجاح.'
    );
    if (saved) {
      setOrder(createOrder(getStandPrice(standPrices, '', false)));
      setEditingOrderId('');
    }
  };

  const editOrder = (selectedOrder) => {
    setOrder({
      ...createOrder(getStandPrice(standPrices, '', false)),
      ...selectedOrder,
      discountPercentage: toAmount(selectedOrder.discountPercentage),
      items: (selectedOrder.items || []).map((item) => ({
        ...createOrderItem(),
        ...item
      }))
    });
    setEditingOrderId(selectedOrder.id);
    setActiveTab('orders');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelOrderEdit = () => {
    setEditingOrderId('');
    setOrder(createOrder(getStandPrice(standPrices, '', false)));
  };

  const deleteOrder = async (selectedOrder) => {
    // Confirmation prevents accidental destructive inventory reconciliation.
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      `هل تريد حذف طلب ${selectedOrder.customerName}؟ سيتم إرجاع الخامات المخصومة إلى المخزون.`
    );
    if (!confirmed) return;
    const deleted = await runSave(
      () => firebase.deleteAccountingOrder(selectedOrder.id),
      'تم حذف الطلب وإرجاع خاماته إلى المخزون بنجاح.'
    );
    if (deleted && editingOrderId === selectedOrder.id) cancelOrderEdit();
  };

  const submitLoss = async (event) => {
    event.preventDefault();
    if (loss.type === 'RAW_MATERIAL' && !loss.materialId) {
      setMessage({ type: 'error', text: 'اختر الخامة التي تم إهلاكها.' });
      return;
    }
    const saved = await runSave(
      () => firebase.addAccountingLoss({
        ...loss,
        quantity: toAmount(loss.quantity),
        amount: toAmount(loss.amount)
      }),
      'تم تسجيل الهالك بنجاح.'
    );
    if (saved) {
      setLoss((current) => ({
        ...current, materialId: '', quantity: 1, amount: 0, note: ''
      }));
    }
  };

  const submitExpense = async (event) => {
    event.preventDefault();
    if (!expense.name.trim() || toAmount(expense.amount) <= 0) {
      setMessage({ type: 'error', text: 'أدخل اسم المصروف وقيمته.' });
      return;
    }
    const saved = await runSave(
      () => firebase.addAccountingExpense(expense),
      'تم تسجيل المصروف بنجاح.'
    );
    if (saved) setExpense((current) => ({ ...current, amount: 0, note: '' }));
  };

  const submitCollection = async (event) => {
    event.preventDefault();
    const collectedAmount = toAmount(collection.amount);
    if (!selectedCollectionOrders.length || collectedAmount <= 0) {
      setMessage({ type: 'error', text: 'حدد طلباً مشحوناً واحداً على الأقل وأدخل إجمالي المبلغ المحصل.' });
      return;
    }
    if (collectedAmount - selectedCollectionTotal > 0.009) {
      setMessage({
        type: 'error',
        text: `المبلغ المحصل أكبر من إجمالي أرصدة الطلبات المحددة، وهو ${formatAccountingMoney(selectedCollectionTotal)}.`
      });
      return;
    }
    const saved = await runSave(
      () => firebase.addAccountingCollectionBatch(
        { ...collection, amount: collectedAmount },
        allocateCollectionAmount(collectedAmount, selectedCollectionOrders, collections)
      ),
      `تم ربط التحصيل بـ ${selectedCollectionOrders.length} طلبات بنجاح.`
    );
    if (saved) {
      setCollection((current) => ({
        ...current, orderIds: [], amount: 0, note: ''
      }));
    }
  };

  const toggleCollectionOrder = (orderId) => setCollection((current) => ({
    ...current,
    orderIds: current.orderIds.includes(orderId)
      ? current.orderIds.filter((id) => id !== orderId)
      : [...current.orderIds, orderId]
  }));

  const submitOffer = async (event) => {
    event.preventDefault();
    if (
      !offer.name.trim()
      || toAmount(offer.quantity) <= 0
      || toAmount(offer.regularUnitPrice) < 0
      || toAmount(offer.offerPrice) < 0
    ) {
      setMessage({ type: 'error', text: 'أدخل اسم العرض والكمية والأسعار بصورة صحيحة.' });
      return;
    }
    const saved = await runSave(
      () => firebase.addAccountingOffer({
        ...offer,
        quantity: toAmount(offer.quantity),
        regularUnitPrice: toAmount(offer.regularUnitPrice),
        offerPrice: toAmount(offer.offerPrice)
      }),
      'تم حفظ العرض بنجاح، وسيُطبق على الطلبات الجديدة فقط.'
    );
    if (saved) setOffer((current) => ({ ...current, note: '' }));
  };

  const submitStandPrices = async (event) => {
    event.preventDefault();
    const normalizedPrices = Object.keys(DEFAULT_STAND_PRICES).reduce((prices, key) => ({
      ...prices,
      [key]: toAmount(standPrices[key])
    }), {});
    if (Object.values(normalizedPrices).some((price) => price < 0)) {
      setMessage({ type: 'error', text: 'يجب إدخال أسعار صحيحة للمنتجات.' });
      return;
    }
    const saved = await runSave(
      () => firebase.updateAccountingStandPrices(normalizedPrices),
      'تم تحديث أسعار الستاندات. ستطبق الأسعار الجديدة على الطلبات الجديدة فقط.'
    );
    if (saved) {
      setOrder((current) => ({
        ...current,
        items: current.items.map((item) => {
          if (item.offerId || item.productCatalogId) return item;
          const material = inventory.find((entry) => entry.id === item.materialId);
          return {
            ...item,
            unitPrice: getStandPrice(normalizedPrices, material ? material.size : '', item.nfc)
          };
        })
      }));
    }
  };

  const normalizeProductCatalog = (items) => items.map((item) => ({
    id: item.id,
    name: String(item.name || '').trim(),
    hasNfcOptions: Boolean(item.hasNfcOptions),
    withoutNfcPrice: toAmount(item.withoutNfcPrice),
    withNfcPrice: item.hasNfcOptions
      ? toAmount(item.withNfcPrice)
      : toAmount(item.withoutNfcPrice)
  }));

  const isValidProductCatalog = (items) => items.every((item) => (
    String(item.name || '').trim()
    && toAmount(item.withoutNfcPrice) >= 0
    && (!item.hasNfcOptions || toAmount(item.withNfcPrice) >= 0)
  ));

  const submitNewProductType = async (event) => {
    event.preventDefault();
    if (!isValidProductCatalog([newProductType])) {
      setMessage({ type: 'error', text: 'أدخل اسم المنتج وسعر بيع صحيحاً.' });
      return;
    }
    const productType = {
      ...newProductType,
      id: `product-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    };
    const savedCatalog = normalizeProductCatalog([...productCatalog, productType]);
    const saved = await runSave(
      () => firebase.updateAccountingProductCatalog(savedCatalog),
      'تمت إضافة نوع المنتج وسيظهر في الطلبات الجديدة.'
    );
    if (saved) setNewProductType(createProductType());
  };

  const saveProductCatalog = async () => {
    if (!isValidProductCatalog(productCatalog)) {
      setMessage({ type: 'error', text: 'راجع أسماء المنتجات وأسعار البيع.' });
      return;
    }
    await runSave(
      () => firebase.updateAccountingProductCatalog(normalizeProductCatalog(productCatalog)),
      'تم حفظ تعديلات أسعار المنتجات. الطلبات القديمة لم تتغير.'
    );
  };

  const deleteProductType = async (productType) => {
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      `هل تريد حذف ${productType.name} من قائمة الأسعار؟ لن تتأثر الطلبات القديمة.`
    );
    if (!confirmed) return;
    await runSave(
      () => firebase.updateAccountingProductCatalog(
        normalizeProductCatalog(productCatalog.filter((item) => item.id !== productType.id))
      ),
      'تم حذف نوع المنتج من قائمة الأسعار فقط.'
    );
  };

  const changeOfferStatus = (offerId, active) => runSave(
    () => firebase.updateAccountingOfferStatus(offerId, active),
    active ? 'تم تفعيل العرض.' : 'تم إيقاف العرض.'
  );

  const applyOfferToOrder = () => {
    const selectedOffer = activeOffers.find((item) => item.id === selectedOfferId);
    if (!selectedOffer) {
      setMessage({ type: 'error', text: 'اختر عرضاً لإضافته إلى الطلب.' });
      return;
    }
    const quantity = toAmount(selectedOffer.quantity);
    const regularUnitPrice = toAmount(selectedOffer.regularUnitPrice);
    const offerPrice = toAmount(selectedOffer.offerPrice);
    const regularTotal = quantity * regularUnitPrice;
    const offeredItem = {
      ...createOrderItem(),
      productName: selectedOffer.name,
      nfc: Boolean(selectedOffer.nfc),
      quantity,
      unitPrice: offerPrice > regularTotal ? offerPrice / quantity : regularUnitPrice,
      discount: Math.max(regularTotal - offerPrice, 0),
      offerId: selectedOffer.id,
      offerName: selectedOffer.name,
      offerPrice,
      offerFreeShipping: Boolean(selectedOffer.freeShipping)
    };

    setOrder((current) => {
      const hasOnlyEmptyDefault = current.items.length === 1
        && !current.items[0].materialId
        && current.items[0].productName === 'ستاند أكريليك';
      return {
        ...current,
        shippingCharged: selectedOffer.freeShipping ? 0 : current.shippingCharged,
        items: hasOnlyEmptyDefault ? [offeredItem] : [...current.items, offeredItem]
      };
    });
    setSelectedOfferId('');
    setMessage({ type: 'success', text: 'تمت إضافة العرض إلى الطلب. اختر الخامة لتحديد المقاس واللون المطلوب خصمهما.' });
  };

  const changeOrdersStatus = (orderIds, status) => runSave(
    () => firebase.updateAccountingOrdersStatus(orderIds, status),
    'تم تغيير حالة الطلبات المحددة إلى تم الشحن.'
  );

  const tabs = [
    ['overview', 'نظرة عامة'],
    ['prices', 'أسعار المنتجات'],
    ['inventory', 'الخامات'],
    ['offers', 'العروض'],
    ['orders', 'الطلبات'],
    ['losses', 'الهالك والخسائر'],
    ['expenses', 'المصروفات'],
    ['collections', 'تحصيلات الشحن'],
    ['report', 'التقرير الشهري']
  ];

  return (
    <Boundary>
      <div className="accounting-page" dir="rtl">
        <div className="accounting-heading">
          <div>
            <h2>الحسابات وإدارة المخزون</h2>
            <p className="text-subtle">سجلات خاصة بالإدارة للمخزون والطلبات والتحصيلات والأرباح.</p>
          </div>
          <button className="button button-small" onClick={loadAccounting} type="button">تحديث</button>
        </div>

        <div className="accounting-tabs">
          {tabs.map(([value, label]) => (
            <button
              className={activeTab === value ? 'accounting-tab accounting-tab-active' : 'accounting-tab'}
              key={value}
              onClick={() => setActiveTab(value)}
              type="button"
            >
              {label}
            </button>
          ))}
        </div>

        {message ? (
          <p className={message.type === 'error' ? 'toast-error' : 'toast-success'}>{message.text}</p>
        ) : null}

        {isLoading ? <div className="loader"><LoadingOutlined /></div> : null}

        {!isLoading && activeTab === 'overview' ? (
          <section>
            <div className="accounting-metrics">
              <Metric label={`عدد طلبات ${reportMonth}`} value={String(report.orderCount)} />
              <Metric label={`الطلبات المشحونة ${reportMonth}`} value={String(monthlyShippedOrderCount)} />
              <Metric label="قيمة مخزون الخامات" value={formatAccountingMoney(inventoryValue)} />
              <Metric label="المبالغ المتبقية لدى العملاء" value={formatAccountingMoney(totalOutstanding)} />
              <Metric label={`إيرادات ${reportMonth}`} value={formatAccountingMoney(report.revenue)} />
              <Metric label={`الشحن المحصل ${reportMonth}`} value={formatAccountingMoney(report.shippingRevenue)} />
              <Metric
                label={`تكلفة الشحن الفعلية ${reportMonth}`}
                tone="negative"
                value={formatAccountingMoney(report.shippingCost)}
              />
              <Metric
                label={`صافي ربح ${reportMonth}`}
                tone={report.netProfit >= 0 ? 'positive' : 'negative'}
                value={formatAccountingMoney(report.netProfit)}
              />
            </div>
            <div className="accounting-panel">
              <h3>أحدث الطلبات</h3>
              <OrdersTable
                bulkSelection={false}
                collections={collections}
                onDelete={deleteOrder}
                onEdit={editOrder}
                onStatusChange={changeOrdersStatus}
                orders={orders.slice(0, 5)}
                saving={isSaving}
              />
            </div>
          </section>
        ) : null}

        {!isLoading && activeTab === 'prices' ? (
          <section className="accounting-stack">
            <form className="accounting-panel" onSubmit={submitStandPrices}>
              <h3>أسعار المنتجات</h3>
              <p className="text-subtle">
                الأسعار الجديدة تطبق على الطلبات الجديدة فقط، ولا تغير أي طلب تم تسجيله سابقاً.
              </p>
              <h4>أسعار الستاندات الأساسية</h4>
              <div className="accounting-price-grid">
                <StandPriceFields
                  label="ستاند صغير 11×18"
                  onChange={(key, value) => setStandPrices({ ...standPrices, [key]: value })}
                  prefix="small"
                  prices={standPrices}
                />
                <StandPriceFields
                  label="ستاند كبير 15×25"
                  onChange={(key, value) => setStandPrices({ ...standPrices, [key]: value })}
                  prefix="large"
                  prices={standPrices}
                />
                <StandPriceFields
                  label="ستاند دائري 10×10"
                  onChange={(key, value) => setStandPrices({ ...standPrices, [key]: value })}
                  prefix="circle"
                  prices={standPrices}
                />
              </div>
              <SaveButton saving={isSaving}>حفظ أسعار الستاندات</SaveButton>
            </form>

            <form className="accounting-panel" onSubmit={submitNewProductType}>
              <h3>إضافة نوع منتج جديد</h3>
              <div className="accounting-form-grid">
                <FormField label="اسم المنتج">
                  <input
                    className="input-form"
                    onChange={(event) => setNewProductType({
                      ...newProductType, name: event.target.value
                    })}
                    placeholder="مثال: ميدالية أكريليك"
                    required
                    value={newProductType.name}
                  />
                </FormField>
                <FormField label="هل يتوفر مع NFC؟">
                  <select
                    className="input-form"
                    onChange={(event) => setNewProductType({
                      ...newProductType,
                      hasNfcOptions: event.target.value === 'true'
                    })}
                    value={String(newProductType.hasNfcOptions)}
                  >
                    <option value="false">لا، سعر واحد</option>
                    <option value="true">نعم، بسعرين</option>
                  </select>
                </FormField>
                <FormField label={newProductType.hasNfcOptions ? 'السعر بدون NFC' : 'سعر البيع'}>
                  <input
                    className="input-form"
                    min="0"
                    onChange={(event) => setNewProductType({
                      ...newProductType, withoutNfcPrice: event.target.value
                    })}
                    step="0.01"
                    type="number"
                    value={newProductType.withoutNfcPrice}
                  />
                </FormField>
                {newProductType.hasNfcOptions ? (
                  <FormField label="السعر مع NFC">
                    <input
                      className="input-form"
                      min="0"
                      onChange={(event) => setNewProductType({
                        ...newProductType, withNfcPrice: event.target.value
                      })}
                      step="0.01"
                      type="number"
                      value={newProductType.withNfcPrice}
                    />
                  </FormField>
                ) : null}
              </div>
              <SaveButton saving={isSaving}>إضافة المنتج</SaveButton>
            </form>

            <div className="accounting-panel">
              <h3>أنواع المنتجات الإضافية</h3>
              <p className="text-subtle">
                يمكنك تعديل الاسم أو السعر، ثم حفظ التعديلات. حذف النوع لا يحذف المنتجات من الطلبات القديمة.
              </p>
              <div className="accounting-price-grid">
                {productCatalog.map((productType) => (
                  <div className="accounting-price-card" key={productType.id}>
                    <FormField label="اسم المنتج">
                      <input
                        className="input-form"
                        onChange={(event) => setProductCatalog((current) => current.map((item) => (
                          item.id === productType.id ? { ...item, name: event.target.value } : item
                        )))}
                        value={productType.name}
                      />
                    </FormField>
                    <FormField label="نوع السعر">
                      <select
                        className="input-form"
                        onChange={(event) => setProductCatalog((current) => current.map((item) => (
                          item.id === productType.id
                            ? { ...item, hasNfcOptions: event.target.value === 'true' }
                            : item
                        )))}
                        value={String(productType.hasNfcOptions)}
                      >
                        <option value="false">سعر واحد</option>
                        <option value="true">بدون/مع NFC</option>
                      </select>
                    </FormField>
                    <FormField label={productType.hasNfcOptions ? 'السعر بدون NFC' : 'سعر البيع'}>
                      <input
                        className="input-form"
                        min="0"
                        onChange={(event) => setProductCatalog((current) => current.map((item) => (
                          item.id === productType.id
                            ? { ...item, withoutNfcPrice: event.target.value }
                            : item
                        )))}
                        step="0.01"
                        type="number"
                        value={productType.withoutNfcPrice}
                      />
                    </FormField>
                    {productType.hasNfcOptions ? (
                      <FormField label="السعر مع NFC">
                        <input
                          className="input-form"
                          min="0"
                          onChange={(event) => setProductCatalog((current) => current.map((item) => (
                            item.id === productType.id
                              ? { ...item, withNfcPrice: event.target.value }
                              : item
                          )))}
                          step="0.01"
                          type="number"
                          value={productType.withNfcPrice}
                        />
                      </FormField>
                    ) : null}
                    <button
                      className="button button-small button-danger"
                      disabled={isSaving}
                      onClick={() => deleteProductType(productType)}
                      type="button"
                    >
                      حذف النوع
                    </button>
                  </div>
                ))}
              </div>
              {!productCatalog.length ? (
                <p className="text-subtle">لم تتم إضافة أنواع منتجات أخرى بعد.</p>
              ) : (
                <button
                  className="button"
                  disabled={isSaving}
                  onClick={saveProductCatalog}
                  type="button"
                >
                  {isSaving ? <LoadingOutlined /> : null}
                  {' '}
                  حفظ تعديلات المنتجات
                </button>
              )}
            </div>
          </section>
        ) : null}

        {!isLoading && activeTab === 'inventory' ? (
          <section className="accounting-two-column">
            <form className="accounting-panel" onSubmit={submitPurchase}>
              <h3>إضافة خامة أو زيادة المخزون</h3>
              <FormField label="اسم الخامة">
                <input
                  className="input-form"
                  onChange={(event) => {
                    setPurchase({ ...purchase, name: event.target.value });
                    setUpdateMaterialPrice(false);
                  }}
                  required
                  value={purchase.name}
                />
              </FormField>
              <FormField label="المقاس">
                <select
                  className="input-form"
                  onChange={(event) => {
                    setPurchase({ ...purchase, size: event.target.value });
                    setUpdateMaterialPrice(false);
                  }}
                  value={purchase.size}
                >
                  <option value="11×18">11×18</option>
                  <option value="15×25">15×25</option>
                  <option value="10×10 Circle">دائرة 10×10</option>
                  <option value="Custom">مقاس مخصص</option>
                </select>
              </FormField>
              {purchase.size === 'Custom' ? (
                <FormField label="المقاس المخصص">
                  <input
                    className="input-form"
                    onChange={(event) => {
                      setPurchase({ ...purchase, customSize: event.target.value });
                      setUpdateMaterialPrice(false);
                    }}
                    placeholder="مثال: 20×30"
                    required
                    value={purchase.customSize}
                  />
                </FormField>
              ) : null}
              <FormField label="اللون">
                <select
                  className="input-form"
                  onChange={(event) => {
                    setPurchase({ ...purchase, color: event.target.value });
                    setUpdateMaterialPrice(false);
                  }}
                  value={purchase.color}
                >
                  <option value="WHITE">أبيض</option>
                  <option value="BLACK">أسود</option>
                </select>
              </FormField>
              <div className="accounting-form-row">
                <FormField label="الكمية">
                  <input
                    className="input-form"
                    min="0.01"
                    onChange={(event) => setPurchase({ ...purchase, quantity: event.target.value })}
                    step="0.01"
                    type="number"
                    value={purchase.quantity}
                  />
                </FormField>
                {!matchingPurchaseItem || updateMaterialPrice ? (
                  <FormField label={matchingPurchaseItem ? 'السعر الثابت الجديد' : 'سعر الخامة أول مرة'}>
                    <input
                      className="input-form"
                      min="0"
                      onChange={(event) => setPurchase({ ...purchase, unitPrice: event.target.value })}
                      step="0.01"
                      type="number"
                      value={purchase.unitPrice}
                    />
                  </FormField>
                ) : (
                  <FormField label="سعر الخامة الثابت">
                    <div className="accounting-fixed-price">
                      <strong>{formatAccountingMoney(matchingPurchaseItem.averageUnitCost)}</strong>
                      <button
                        className="button button-small button-muted"
                        onClick={() => {
                          setPurchase({
                            ...purchase,
                            unitPrice: toAmount(matchingPurchaseItem.averageUnitCost)
                          });
                          setUpdateMaterialPrice(true);
                        }}
                        type="button"
                      >
                        تحديث السعر
                      </button>
                    </div>
                  </FormField>
                )}
              </div>
              <FormField label="تاريخ الشراء">
                <input
                  className="input-form"
                  onChange={(event) => setPurchase({ ...purchase, date: event.target.value })}
                  type="date"
                  value={purchase.date}
                />
              </FormField>
              <FormField label="ملاحظات">
                <input
                  className="input-form"
                  onChange={(event) => setPurchase({ ...purchase, note: event.target.value })}
                  value={purchase.note}
                />
              </FormField>
              <SaveButton saving={isSaving}>حفظ عملية الشراء</SaveButton>
            </form>

            <div className="accounting-panel">
              <h3>مخزون الخامات الحالي</h3>
              <div className="accounting-stack">
                {inventory.map((item) => (
                  <div className="accounting-list-card" key={item.id}>
                    <strong>
                      {item.name}
                      {' '}
                      —
                      {' '}
                      {item.size}
                      {' '}
                      —
                      {' '}
                      {COLOR_LABELS[item.color] || item.color}
                    </strong>
                    <span>
                      {toAmount(item.quantity)}
                      {' '}
                      قطعة
                    </span>
                    <small>
                      السعر الثابت
                      {' '}
                      {formatAccountingMoney(item.averageUnitCost)}
                      {' '}
                      · القيمة
                      {' '}
                      {formatAccountingMoney(item.inventoryValue)}
                    </small>
                  </div>
                ))}
                {!inventory.length ? <p className="text-subtle">لم تتم إضافة خامات بعد.</p> : null}
              </div>
              <h4 className="accounting-section-gap">سجل المشتريات</h4>
              <div className="accounting-stack">
                {purchases.slice(0, 20).map((item) => (
                  <div className="accounting-list-card" key={item.id}>
                    <strong>
                      {item.name}
                      {' '}
                      —
                      {' '}
                      {item.size}
                      {' '}
                      —
                      {' '}
                      {COLOR_LABELS[item.color] || item.color}
                    </strong>
                    <small>
                      {item.date}
                      {' '}
                      ·
                      {item.quantity}
                      {' '}
                      قطعة ×
                      {formatAccountingMoney(item.unitPrice)}
                      {' '}
                      =
                      {formatAccountingMoney(item.total)}
                    </small>
                  </div>
                ))}
                {!purchases.length ? <p className="text-subtle">لم يتم تسجيل مشتريات بعد.</p> : null}
              </div>
            </div>
          </section>
        ) : null}

        {!isLoading && activeTab === 'offers' ? (
          <section className="accounting-two-column">
            <form className="accounting-panel" onSubmit={submitOffer}>
              <h3>إنشاء عرض جديد</h3>
              <p className="text-subtle">
                يتم نسخ تفاصيل العرض داخل الطلب الجديد، لذلك لن تتغير الطلبات القديمة عند إضافة أو إيقاف أي عرض.
              </p>
              <FormField label="اسم العرض">
                <input
                  className="input-form"
                  onChange={(event) => setOffer({ ...offer, name: event.target.value })}
                  required
                  value={offer.name}
                />
              </FormField>
              <div className="accounting-form-grid">
                <FormField label="عدد القطع في العرض">
                  <input
                    className="input-form"
                    min="1"
                    onChange={(event) => setOffer({ ...offer, quantity: event.target.value })}
                    type="number"
                    value={offer.quantity}
                  />
                </FormField>
                <FormField label="السعر العادي للقطعة">
                  <input
                    className="input-form"
                    min="0"
                    onChange={(event) => setOffer({ ...offer, regularUnitPrice: event.target.value })}
                    step="0.01"
                    type="number"
                    value={offer.regularUnitPrice}
                  />
                </FormField>
                <FormField label="إجمالي سعر العرض">
                  <input
                    className="input-form"
                    min="0"
                    onChange={(event) => setOffer({ ...offer, offerPrice: event.target.value })}
                    step="0.01"
                    type="number"
                    value={offer.offerPrice}
                  />
                </FormField>
                <FormField label="خاصية NFC">
                  <select
                    className="input-form"
                    onChange={(event) => setOffer({ ...offer, nfc: event.target.value === 'true' })}
                    value={String(offer.nfc)}
                  >
                    <option value="true">مع NFC</option>
                    <option value="false">بدون NFC</option>
                  </select>
                </FormField>
                <FormField label="الشحن للعميل">
                  <select
                    className="input-form"
                    onChange={(event) => setOffer({
                      ...offer, freeShipping: event.target.value === 'true'
                    })}
                    value={String(offer.freeShipping)}
                  >
                    <option value="true">شحن مجاني</option>
                    <option value="false">شحن مدفوع</option>
                  </select>
                </FormField>
              </div>
              <FormField label="ملاحظات العرض">
                <input
                  className="input-form"
                  onChange={(event) => setOffer({ ...offer, note: event.target.value })}
                  value={offer.note}
                />
              </FormField>
              <div className="accounting-offer-preview">
                <span>
                  السعر قبل العرض:
                  {' '}
                  {formatAccountingMoney(toAmount(offer.quantity) * toAmount(offer.regularUnitPrice))}
                </span>
                <strong>
                  السعر بعد العرض:
                  {' '}
                  {formatAccountingMoney(offer.offerPrice)}
                </strong>
              </div>
              <SaveButton saving={isSaving}>حفظ العرض</SaveButton>
            </form>

            <div className="accounting-panel">
              <h3>العروض المسجلة</h3>
              <div className="accounting-stack">
                {offers.map((item) => (
                  <div className="accounting-list-card accounting-offer-card" key={item.id}>
                    <div>
                      <strong>{item.name}</strong>
                      <small>
                        {item.quantity}
                        {' '}
                        قطع · السعر العادي
                        {' '}
                        {formatAccountingMoney(toAmount(item.quantity) * toAmount(item.regularUnitPrice))}
                        {' '}
                        · العرض
                        {' '}
                        {formatAccountingMoney(item.offerPrice)}
                        {' '}
                        ·
                        {' '}
                        {item.nfc ? 'مع NFC' : 'بدون NFC'}
                        {' '}
                        ·
                        {' '}
                        {item.freeShipping ? 'شحن مجاني' : 'شحن مدفوع'}
                      </small>
                    </div>
                    <button
                      className={item.active !== false ? 'accounting-status accounting-status-shipped' : 'accounting-status'}
                      disabled={isSaving}
                      onClick={() => changeOfferStatus(item.id, item.active === false)}
                      type="button"
                    >
                      {item.active !== false ? 'مفعل' : 'متوقف'}
                    </button>
                  </div>
                ))}
                {!offers.length ? <p className="text-subtle">لم تتم إضافة عروض بعد.</p> : null}
              </div>
            </div>
          </section>
        ) : null}

        {!isLoading && activeTab === 'orders' ? (
          <section>
            <form className="accounting-panel" onSubmit={submitOrder}>
              <div className="accounting-order-form-heading">
                <h3>{editingOrderId ? 'تعديل طلب العميل' : 'تسجيل طلب عميل'}</h3>
                {editingOrderId ? (
                  <button
                    className="button button-small button-muted"
                    onClick={cancelOrderEdit}
                    type="button"
                  >
                    إلغاء التعديل
                  </button>
                ) : null}
              </div>
              <div className="accounting-form-grid">
                <FormField label="اسم العميل">
                  <input
                    className="input-form"
                    onChange={(event) => setOrder({ ...order, customerName: event.target.value })}
                    required
                    value={order.customerName}
                  />
                </FormField>
                <FormField label="تاريخ الطلب">
                  <input
                    className="input-form"
                    onChange={(event) => setOrder({ ...order, date: event.target.value })}
                    type="date"
                    value={order.date}
                  />
                </FormField>
                <FormField label="العربون المدفوع">
                  <input
                    className="input-form"
                    min="0"
                    onChange={(event) => setOrder({ ...order, deposit: event.target.value })}
                    step="0.01"
                    type="number"
                    value={order.deposit}
                  />
                </FormField>
                <FormField label="حالة الطلب">
                  <select
                    className="input-form"
                    onChange={(event) => setOrder({ ...order, status: event.target.value })}
                    value={order.status}
                  >
                    <option value="PENDING">قيد التجهيز</option>
                    <option value="SHIPPED">تم الشحن</option>
                  </select>
                </FormField>
                <FormField label="تكلفة الشحن الفعلية">
                  <input
                    className="input-form"
                    min="0"
                    onChange={(event) => setOrder({ ...order, shippingCost: event.target.value })}
                    step="0.01"
                    type="number"
                    value={order.shippingCost}
                  />
                </FormField>
                <FormField label="الشحن المحصل من العميل">
                  <input
                    className="input-form"
                    min="0"
                    onChange={(event) => setOrder({ ...order, shippingCharged: event.target.value })}
                    step="0.01"
                    type="number"
                    value={order.shippingCharged}
                  />
                </FormField>
                <FormField label="خصم على إجمالي الطلب (%)">
                  <input
                    className="input-form"
                    max="100"
                    min="0"
                    onChange={(event) => setOrder({
                      ...order, discountPercentage: event.target.value
                    })}
                    step="0.01"
                    type="number"
                    value={order.discountPercentage}
                  />
                </FormField>
              </div>

              <div className="accounting-apply-offer">
                <FormField label="إضافة عرض إلى هذا الطلب">
                  <select
                    className="input-form"
                    onChange={(event) => setSelectedOfferId(event.target.value)}
                    value={selectedOfferId}
                  >
                    <option value="">اختر عرضاً</option>
                    {activeOffers.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                        {' '}
                        —
                        {' '}
                        {formatAccountingMoney(item.offerPrice)}
                        {item.freeShipping ? ' — شحن مجاني' : ''}
                      </option>
                    ))}
                  </select>
                </FormField>
                <button
                  className="button button-small"
                  disabled={!selectedOfferId}
                  onClick={applyOfferToOrder}
                  type="button"
                >
                  تطبيق العرض
                </button>
              </div>

              <h4>منتجات الطلب</h4>
              {order.items.map((item, index) => (
                <div className="accounting-order-item" key={`order-item-${index + 1}`}>
                  {item.offerName ? (
                    <div className="accounting-offer-badge">
                      عرض:
                      {' '}
                      {item.offerName}
                      {item.offerFreeShipping ? ' · شحن مجاني' : ''}
                    </div>
                  ) : null}
                  <div className="accounting-form-grid">
                    <FormField label="اختيار من قائمة أسعار المنتجات">
                      <select
                        className="input-form"
                        onChange={(event) => {
                          const productCatalogId = event.target.value;
                          const productType = productCatalog.find(
                            (entry) => entry.id === productCatalogId
                          );
                          updateOrderItem(index, productType ? {
                            productCatalogId,
                            productName: productType.name,
                            nfc: false,
                            unitPrice: getProductTypePrice(productType, false)
                          } : {
                            productCatalogId: '',
                            productName: 'ستاند أكريليك',
                            nfc: false,
                            unitPrice: getStandPrice(
                              standPrices,
                              (inventory.find(
                                (entry) => entry.id === item.materialId
                              ) || {}).size,
                              false
                            )
                          });
                        }}
                        value={item.productCatalogId || ''}
                      >
                        <option value="">ستاند أكريليك — السعر حسب المقاس</option>
                        {productCatalog.map((productType) => (
                          <option key={productType.id} value={productType.id}>
                            {productType.name}
                            {' — '}
                            {formatAccountingMoney(productType.withoutNfcPrice)}
                          </option>
                        ))}
                      </select>
                    </FormField>
                    <FormField label="المنتج أو الوصف">
                      <input
                        className="input-form"
                        onChange={(event) => updateOrderItem(index, { productName: event.target.value })}
                        value={item.productName}
                      />
                    </FormField>
                    <FormField label="الخامة المطلوب خصمها">
                      <select
                        className="input-form"
                        onChange={(event) => {
                          const materialId = event.target.value;
                          updateOrderItem(index, {
                            materialId,
                            unitPrice: item.offerId
                              ? item.unitPrice
                              : getOrderItemPrice(item, item.nfc, materialId)
                          });
                        }}
                        value={item.materialId}
                      >
                        <option value="">منتج مخصص بدون خصم تلقائي من المخزون</option>
                        {inventory.map((material) => (
                          <option key={material.id} value={material.id}>
                            {material.name}
                            {' '}
                            —
                            {material.size}
                            {' '}
                            —
                            {COLOR_LABELS[material.color] || material.color}
                            {' '}
                            (
                            {material.quantity}
                            {' '}
                            متاح)
                          </option>
                        ))}
                      </select>
                    </FormField>
                    <FormField label="NFC">
                      <select
                        className="input-form"
                        disabled={Boolean(
                          item.productCatalogId
                          && !(productCatalog.find(
                            (entry) => entry.id === item.productCatalogId
                          ) || {}).hasNfcOptions
                        )}
                        onChange={(event) => updateOrderItem(index, {
                          nfc: event.target.value === 'true',
                          unitPrice: item.offerId
                            ? item.unitPrice
                            : getOrderItemPrice(item, event.target.value === 'true')
                        })}
                        value={String(item.nfc)}
                      >
                        <option value="false">بدون NFC</option>
                        <option value="true">مع NFC</option>
                      </select>
                    </FormField>
                    <FormField label="الكمية">
                      <input
                        className="input-form"
                        min="1"
                        onChange={(event) => updateOrderItem(index, { quantity: event.target.value })}
                        type="number"
                        value={item.quantity}
                      />
                    </FormField>
                    <FormField label="سعر بيع القطعة">
                      <input
                        className="input-form"
                        min="0"
                        onChange={(event) => updateOrderItem(index, { unitPrice: event.target.value })}
                        step="0.01"
                        type="number"
                        value={item.unitPrice}
                      />
                    </FormField>
                    <FormField label="خصم هذا المنتج">
                      <input
                        className="input-form"
                        min="0"
                        onChange={(event) => updateOrderItem(index, { discount: event.target.value })}
                        step="0.01"
                        type="number"
                        value={item.discount}
                      />
                    </FormField>
                    {item.materialId ? (
                      <FormField label="عدد قطع الخام لكل منتج">
                        <input
                          className="input-form"
                          min="0.01"
                          onChange={(event) => updateOrderItem(index, {
                            materialQuantityPerUnit: event.target.value
                          })}
                          step="0.01"
                          type="number"
                          value={item.materialQuantityPerUnit}
                        />
                      </FormField>
                    ) : (
                      <FormField label="تكلفة القطعة المخصصة">
                        <input
                          className="input-form"
                          min="0"
                          onChange={(event) => updateOrderItem(index, { customUnitCost: event.target.value })}
                          step="0.01"
                          type="number"
                          value={item.customUnitCost}
                        />
                      </FormField>
                    )}
                  </div>
                  {order.items.length > 1 ? (
                    <button
                      className="button button-small button-muted"
                      onClick={() => setOrder((current) => ({
                        ...current,
                        items: current.items.filter((unusedItem, itemIndex) => itemIndex !== index)
                      }))}
                      type="button"
                    >
                      حذف المنتج
                    </button>
                  ) : null}
                </div>
              ))}
              <button
                className="button button-small button-muted"
                onClick={() => setOrder((current) => ({
                  ...current,
                  items: [
                    ...current.items,
                    createOrderItem(getStandPrice(standPrices, '', false))
                  ]
                }))}
                type="button"
              >
                <PlusOutlined />
                {' '}
                إضافة منتج آخر
              </button>
              <FormField label="ملاحظات الطلب">
                <textarea
                  className="input-form"
                  onChange={(event) => setOrder({ ...order, notes: event.target.value })}
                  value={order.notes}
                />
              </FormField>
              <SaveButton saving={isSaving}>
                {editingOrderId ? 'حفظ تعديلات الطلب' : 'تسجيل الطلب وخصم المخزون'}
              </SaveButton>
            </form>

            <div className="accounting-panel accounting-section-gap">
              <h3>الطلبات المسجلة</h3>
              <OrdersTable
                bulkSelection
                collections={collections}
                onDelete={deleteOrder}
                onEdit={editOrder}
                onStatusChange={changeOrdersStatus}
                orders={orders}
                saving={isSaving}
              />
            </div>
          </section>
        ) : null}

        {!isLoading && activeTab === 'losses' ? (
          <section className="accounting-two-column">
            <form className="accounting-panel" onSubmit={submitLoss}>
              <h3>تسجيل هالك أو خسارة</h3>
              <FormField label="نوع الخسارة">
                <select
                  className="input-form"
                  onChange={(event) => setLoss({ ...loss, type: event.target.value })}
                  value={loss.type}
                >
                  <option value="RAW_MATERIAL">خامات</option>
                  <option value="COMPLETED_STAND">ستاند مكتمل</option>
                  <option value="MONEY">أموال</option>
                </select>
              </FormField>
              {loss.type === 'RAW_MATERIAL' ? (
                <>
                  <FormField label="الخامة">
                    <select
                      className="input-form"
                      onChange={(event) => setLoss({ ...loss, materialId: event.target.value })}
                      value={loss.materialId}
                    >
                      <option value="">اختر الخامة</option>
                      {inventory.map((material) => (
                        <option key={material.id} value={material.id}>
                          {material.name}
                          {' '}
                          —
                          {material.size}
                          {' '}
                          —
                          {COLOR_LABELS[material.color] || material.color}
                          {' '}
                          (
                          {material.quantity}
                          )
                        </option>
                      ))}
                    </select>
                  </FormField>
                  <FormField label="الكمية الهالكة">
                    <input
                      className="input-form"
                      min="0.01"
                      onChange={(event) => setLoss({ ...loss, quantity: event.target.value })}
                      step="0.01"
                      type="number"
                      value={loss.quantity}
                    />
                  </FormField>
                </>
              ) : (
                <FormField label="قيمة الخسارة">
                  <input
                    className="input-form"
                    min="0"
                    onChange={(event) => setLoss({ ...loss, amount: event.target.value })}
                    step="0.01"
                    type="number"
                    value={loss.amount}
                  />
                </FormField>
              )}
              <FormField label="تاريخ الخسارة">
                <input
                  className="input-form"
                  onChange={(event) => setLoss({ ...loss, date: event.target.value })}
                  type="date"
                  value={loss.date}
                />
              </FormField>
              <FormField label="السبب أو الملاحظات">
                <textarea
                  className="input-form"
                  onChange={(event) => setLoss({ ...loss, note: event.target.value })}
                  required
                  value={loss.note}
                />
              </FormField>
              <SaveButton saving={isSaving}>تسجيل الخسارة</SaveButton>
            </form>
            <RecordList
              empty="لم يتم تسجيل خسائر بعد."
              items={losses}
              render={(item) => ({
                title: `${LOSS_TYPE_LABELS[item.type] || item.type} — ${getArabicMaterialName(item.materialName) || item.note}`,
                detail: `${item.date} · ${formatAccountingMoney(item.calculatedAmount || item.amount)}`
              })}
              title="سجل الهالك والخسائر"
            />
          </section>
        ) : null}

        {!isLoading && activeTab === 'expenses' ? (
          <section className="accounting-two-column">
            <form className="accounting-panel" onSubmit={submitExpense}>
              <h3>إضافة مصروف</h3>
              <FormField label="التصنيف">
                <select
                  className="input-form"
                  onChange={(event) => setExpense({ ...expense, category: event.target.value })}
                  value={expense.category}
                >
                  <option value="ADVERTISING">إعلانات ممولة</option>
                  <option value="SALARY">راتب أو عمولة</option>
                  <option value="OTHER">مصروف آخر</option>
                </select>
              </FormField>
              <FormField label="اسم المصروف">
                <input
                  className="input-form"
                  onChange={(event) => setExpense({ ...expense, name: event.target.value })}
                  required
                  value={expense.name}
                />
              </FormField>
              <FormField label="المبلغ">
                <input
                  className="input-form"
                  min="0.01"
                  onChange={(event) => setExpense({ ...expense, amount: event.target.value })}
                  step="0.01"
                  type="number"
                  value={expense.amount}
                />
              </FormField>
              <FormField label="تاريخ الدفع">
                <input
                  className="input-form"
                  onChange={(event) => setExpense({ ...expense, date: event.target.value })}
                  type="date"
                  value={expense.date}
                />
              </FormField>
              <FormField label="ملاحظات أو اسم الموظف">
                <input
                  className="input-form"
                  onChange={(event) => setExpense({ ...expense, note: event.target.value })}
                  value={expense.note}
                />
              </FormField>
              <SaveButton saving={isSaving}>حفظ المصروف</SaveButton>
            </form>
            <RecordList
              empty="لم يتم تسجيل مصروفات بعد."
              items={expenses}
              render={(item) => ({
                title: `${EXPENSE_CATEGORY_LABELS[item.category] || item.category} — ${item.name}`,
                detail: `${item.date} · ${formatAccountingMoney(item.amount)}${item.note ? ` · ${item.note}` : ''}`
              })}
              title="سجل المصروفات"
            />
          </section>
        ) : null}

        {!isLoading && activeTab === 'collections' ? (
          <section className="accounting-two-column">
            <form className="accounting-panel" onSubmit={submitCollection}>
              <h3>إضافة تحصيل من شركة الشحن</h3>
              <p className="text-subtle">
                أدخل إجمالي التحصيل مرة واحدة، ثم حدد كل الطلبات التابعة لهذه الدفعة.
              </p>
              <FormField label="إجمالي المبلغ المستلم">
                <input
                  className="input-form"
                  min="0.01"
                  onChange={(event) => setCollection({ ...collection, amount: event.target.value })}
                  step="0.01"
                  type="number"
                  value={collection.amount}
                />
              </FormField>
              <div className="accounting-collection-heading">
                <strong>الطلبات التي تم شحنها</strong>
                {collectibleOrders.length ? (
                  <button
                    className="button button-small button-muted"
                    onClick={() => setCollection((current) => ({
                      ...current,
                      orderIds: current.orderIds.length === collectibleOrders.length
                        ? []
                        : collectibleOrders.map((item) => item.id)
                    }))}
                    type="button"
                  >
                    {collection.orderIds.length === collectibleOrders.length
                      ? 'إلغاء تحديد الكل'
                      : 'تحديد الكل'}
                  </button>
                ) : null}
              </div>
              <div className="accounting-collection-orders">
                {collectibleOrders.map((item) => (
                  // The whole card toggles the associated checkbox.
                  // eslint-disable-next-line jsx-a11y/label-has-associated-control
                  <label
                    className={collection.orderIds.includes(item.id)
                      ? 'accounting-collection-order accounting-collection-order-selected'
                      : 'accounting-collection-order'}
                    key={item.id}
                  >
                    <input
                      checked={collection.orderIds.includes(item.id)}
                      onChange={() => toggleCollectionOrder(item.id)}
                      type="checkbox"
                    />
                    <span>
                      <strong>{item.customerName}</strong>
                      <small>{item.date}</small>
                    </span>
                    <b>{formatAccountingMoney(getOrderOutstanding(item, collections))}</b>
                  </label>
                ))}
                {!collectibleOrders.length ? (
                  <p className="text-subtle">لا توجد طلبات مشحونة لها مبالغ متبقية حالياً.</p>
                ) : null}
              </div>
              <div className="accounting-collection-total">
                <span>
                  الطلبات المحددة:
                  {' '}
                  {selectedCollectionOrders.length}
                </span>
                <strong>
                  إجماليها:
                  {' '}
                  {formatAccountingMoney(selectedCollectionTotal)}
                </strong>
                {toAmount(collection.amount) > 0
                  && selectedCollectionTotal - toAmount(collection.amount) > 0.009 ? (
                    <small>
                      المتبقي على الطلبات بعد توزيع هذا التحصيل:
                      {' '}
                      {formatAccountingMoney(selectedCollectionTotal - toAmount(collection.amount))}
                    </small>
                  ) : null}
              </div>
              <FormField label="تاريخ التحصيل">
                <input
                  className="input-form"
                  onChange={(event) => setCollection({ ...collection, date: event.target.value })}
                  type="date"
                  value={collection.date}
                />
              </FormField>
              <FormField label="ملاحظات أو رقم التحويل">
                <input
                  className="input-form"
                  onChange={(event) => setCollection({ ...collection, note: event.target.value })}
                  value={collection.note}
                />
              </FormField>
              <SaveButton saving={isSaving}>حفظ التحصيل وربطه بالطلبات المحددة</SaveButton>
            </form>
            <RecordList
              empty="لم يتم تسجيل تحصيلات شحن بعد."
              items={collectionRecords}
              render={(item) => ({
                title: item.isBatch
                  ? `تحصيل جماعي — ${item.customerNames.length} طلبات`
                  : item.customerNames[0],
                detail: `${item.date} · ${formatAccountingMoney(item.amount)} · ${item.customerNames.join('، ')}${item.note ? ` · ${item.note}` : ''}`
              })}
              title="سجل تحصيلات شركة الشحن"
            />
          </section>
        ) : null}

        {!isLoading && activeTab === 'report' ? (
          <section>
            <div className="accounting-panel accounting-report-filter">
              <FormField label="شهر التقرير">
                <input
                  className="input-form"
                  onChange={(event) => setReportMonth(event.target.value)}
                  type="month"
                  value={reportMonth}
                />
              </FormField>
            </div>
            <div className="accounting-metrics">
              <Metric label="إيرادات المبيعات" value={formatAccountingMoney(report.productRevenue)} />
              <Metric label="الشحن المحصل من العملاء" value={formatAccountingMoney(report.shippingRevenue)} />
              <Metric label="تكلفة الخامات" tone="negative" value={formatAccountingMoney(report.materialCost)} />
              <Metric label="تكلفة الشحن الفعلية" tone="negative" value={formatAccountingMoney(report.shippingCost)} />
              <Metric label="الهالك والخسائر" tone="negative" value={formatAccountingMoney(report.lossCost)} />
              <Metric label="الإعلانات والرواتب والمصروفات" tone="negative" value={formatAccountingMoney(report.expenseCost)} />
              <Metric label="إجمالي الإيرادات المتوقع" value={formatAccountingMoney(report.revenue)} />
              <Metric label="إجمالي التكاليف" tone="negative" value={formatAccountingMoney(report.totalCosts)} />
              <Metric
                label="صافي الربح المتوقع"
                tone={report.netProfit >= 0 ? 'positive' : 'negative'}
                value={formatAccountingMoney(report.netProfit)}
              />
            </div>
            <div className="accounting-panel accounting-section-gap">
              <h3>النتائج الفعلية حسب التحصيل</h3>
              <p className="text-subtle">
                تعتمد على العربون وتحصيلات شركة الشحن التي تم استلامها فعلياً خلال الشهر.
              </p>
              <div className="accounting-metrics accounting-metrics-actual">
                <Metric
                  label="إجمالي الإيرادات المحصلة فعلياً"
                  tone="positive"
                  value={formatAccountingMoney(report.actualRevenue)}
                />
                <Metric
                  label="صافي الربح الفعلي"
                  tone={report.actualNetProfit >= 0 ? 'positive' : 'negative'}
                  value={formatAccountingMoney(report.actualNetProfit)}
                />
                <Metric
                  label="النقدية المحصلة فعلياً"
                  tone="positive"
                  value={formatAccountingMoney(report.cashCollected)}
                />
              </div>
            </div>
            <div className="accounting-panel accounting-report-note">
              <strong>
                {report.orderCount}
                {' '}
                طلبات خلال
                {' '}
                {reportMonth}
              </strong>
              <p>
                صافي الربح = المبيعات + الشحن المحصل − تكلفة الخامات − تكلفة الشحن الفعلية − الخسائر − المصروفات.
                الربح المتوقع يعتمد على قيمة فواتير الشهر، بينما الربح الفعلي يعتمد على النقدية التي تم تحصيلها فعلاً
                ناقص تكاليف ومصروفات الشهر. العربون وتحصيلات شركة الشحن لا تتم إضافتها مرتين.
              </p>
            </div>
          </section>
        ) : null}
      </div>
    </Boundary>
  );
};

const FormField = ({ children, label }) => (
  // Children contain the associated form control.
  // eslint-disable-next-line jsx-a11y/label-has-associated-control
  <label className="accounting-field">
    <span>{label}</span>
    {children}
  </label>
);

const StandPriceFields = ({
  label, onChange, prefix, prices
}) => (
  <div className="accounting-price-card">
    <h4>{label}</h4>
    <FormField label="بدون NFC">
      <input
        className="input-form"
        min="0"
        onChange={(event) => onChange(`${prefix}WithoutNfc`, event.target.value)}
        step="0.01"
        type="number"
        value={prices[`${prefix}WithoutNfc`]}
      />
    </FormField>
    <FormField label="مع NFC">
      <input
        className="input-form"
        min="0"
        onChange={(event) => onChange(`${prefix}WithNfc`, event.target.value)}
        step="0.01"
        type="number"
        value={prices[`${prefix}WithNfc`]}
      />
    </FormField>
  </div>
);

const SaveButton = ({ children, saving }) => (
  <button className="button" disabled={saving} type="submit">
    {saving ? <LoadingOutlined /> : null}
    {' '}
    {children}
  </button>
);

const Metric = ({ label, value, tone }) => (
  <div className={`accounting-metric${tone ? ` accounting-metric-${tone}` : ''}`}>
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const RecordList = ({
  empty, items, render, title
}) => (
  <div className="accounting-panel">
    <h3>{title}</h3>
    <div className="accounting-stack">
      {items.map((item) => {
        const display = render(item);
        return (
          <div className="accounting-list-card" key={item.id}>
            <strong>{display.title}</strong>
            <small>{display.detail}</small>
          </div>
        );
      })}
      {!items.length ? <p className="text-subtle">{empty}</p> : null}
    </div>
  </div>
);

export const OrdersTable = ({
  bulkSelection, collections, onDelete, onEdit, onStatusChange, orders, saving
}) => {
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const pendingOrderIds = orders
    .filter((order) => order.status !== 'SHIPPED')
    .map((order) => order.id);
  const selectedPendingOrderIds = selectedOrderIds.filter(
    (orderId) => pendingOrderIds.includes(orderId)
  );
  const allPendingSelected = pendingOrderIds.length > 0
    && pendingOrderIds.every((orderId) => selectedPendingOrderIds.includes(orderId));

  const toggleOrder = (orderId) => setSelectedOrderIds((current) => (
    current.includes(orderId)
      ? current.filter((selectedId) => selectedId !== orderId)
      : [...current, orderId]
  ));

  const toggleAllPendingOrders = () => setSelectedOrderIds(
    allPendingSelected ? [] : pendingOrderIds
  );

  const shipSelectedOrders = async () => {
    const saved = await onStatusChange(selectedPendingOrderIds, 'SHIPPED');
    if (saved) setSelectedOrderIds([]);
  };

  return (
    <>
      {bulkSelection ? (
        <div className="accounting-order-bulk-actions">
          <button
            className="button button-small button-muted"
            disabled={saving || pendingOrderIds.length === 0}
            onClick={toggleAllPendingOrders}
            type="button"
          >
            {allPendingSelected ? 'إلغاء تحديد الكل' : 'تحديد كل قيد التجهيز'}
          </button>
          <button
            className="button button-small"
            disabled={saving || selectedPendingOrderIds.length === 0}
            onClick={shipSelectedOrders}
            type="button"
          >
            {saving ? <LoadingOutlined /> : null}
            {' '}
            تحويل المحدد إلى تم الشحن
            {selectedPendingOrderIds.length > 0 ? ` (${selectedPendingOrderIds.length})` : ''}
          </button>
          <span className="text-subtle">اضغط على أي طلب قيد التجهيز لتحديده أو إلغاء تحديده</span>
        </div>
      ) : null}
      <div className="accounting-table-wrap">
        <table className="accounting-table">
          <thead>
            <tr>
              <th>التاريخ</th>
              <th>العميل</th>
              <th>المنتجات</th>
              <th>قيمة الفاتورة</th>
              <th>المحصل</th>
              <th>المتبقي</th>
              <th>الحالة</th>
              <th>الإجراءات</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => {
              const isSelectable = bulkSelection && order.status !== 'SHIPPED' && !saving;
              const isSelected = selectedPendingOrderIds.includes(order.id);
              return (
                <tr
                  aria-selected={bulkSelection ? isSelected : undefined}
                  className={`${isSelectable ? 'accounting-order-row-selectable' : ''}${isSelected
                    ? ' accounting-order-row-selected'
                    : ''}`}
                  key={order.id}
                  onClick={isSelectable ? () => toggleOrder(order.id) : undefined}
                  onKeyDown={isSelectable ? (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      toggleOrder(order.id);
                    }
                  } : undefined}
                  tabIndex={isSelectable ? 0 : undefined}
                >
                  <td>{order.date}</td>
                  <td>{order.customerName}</td>
                  <td>
                    {(order.items || []).map((item, index) => (
                      <small className="accounting-order-summary" key={`${order.id}-${index + 1}`}>
                        {item.quantity}
                        ×
                        {item.productName}
                        {' '}
                        {item.nfc ? '(مع NFC)' : '(بدون NFC)'}
                        {item.materialName ? ` — ${getArabicMaterialName(item.materialName)}` : ''}
                      </small>
                    ))}
                  </td>
                  <td>
                    {formatAccountingMoney(getOrderTotals(order).invoiceTotal)}
                    {toAmount(order.discountPercentage) > 0 ? (
                      <small className="accounting-order-summary">
                        خصم
                        {' '}
                        {toAmount(order.discountPercentage)}
                        %
                      </small>
                    ) : null}
                  </td>
                  <td>{formatAccountingMoney(getOrderCollected(order, collections))}</td>
                  <td>{formatAccountingMoney(getOrderOutstanding(order, collections))}</td>
                  <td>
                    <span
                      className={order.status === 'SHIPPED' ? 'accounting-status accounting-status-shipped' : 'accounting-status'}
                    >
                      {order.status === 'SHIPPED' ? 'تم الشحن' : 'قيد التجهيز'}
                    </span>
                  </td>
                  <td>
                    <div className="accounting-order-actions">
                      <button
                        className="button button-small button-muted"
                        disabled={saving}
                        onClick={(event) => {
                          event.stopPropagation();
                          onEdit(order);
                        }}
                        type="button"
                      >
                        تعديل
                      </button>
                      <button
                        className="button button-small button-danger"
                        disabled={saving}
                        onClick={(event) => {
                          event.stopPropagation();
                          onDelete(order);
                        }}
                        type="button"
                      >
                        حذف
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!orders.length ? (
              <tr>
                <td className="text-subtle" colSpan="8">
                  لم يتم تسجيل طلبات بعد.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </>
  );
};

FormField.propTypes = {
  children: PropType.node.isRequired,
  label: PropType.string.isRequired
};

StandPriceFields.propTypes = {
  label: PropType.string.isRequired,
  onChange: PropType.func.isRequired,
  prefix: PropType.string.isRequired,
  prices: PropType.shape({}).isRequired
};

SaveButton.propTypes = {
  children: PropType.node.isRequired,
  saving: PropType.bool.isRequired
};

Metric.defaultProps = {
  tone: ''
};

Metric.propTypes = {
  label: PropType.string.isRequired,
  tone: PropType.string,
  value: PropType.string.isRequired
};

RecordList.propTypes = {
  empty: PropType.string.isRequired,
  items: PropType.arrayOf(PropType.shape({ id: PropType.string.isRequired })).isRequired,
  render: PropType.func.isRequired,
  title: PropType.string.isRequired
};

OrdersTable.propTypes = {
  bulkSelection: PropType.bool,
  collections: PropType.arrayOf(PropType.shape({ id: PropType.string.isRequired })).isRequired,
  onDelete: PropType.func.isRequired,
  onEdit: PropType.func.isRequired,
  onStatusChange: PropType.func.isRequired,
  orders: PropType.arrayOf(PropType.shape({ id: PropType.string.isRequired })).isRequired,
  saving: PropType.bool.isRequired
};

OrdersTable.defaultProps = {
  bulkSelection: false
};

export default Accounting;
